/**
 * Beauty Burst approved color system (Phase 0/1 approved tokens).
 * These hex values are contractual brand values and must not be changed
 * without founder approval.
 */
export const color = {
  pink: '#F94871',
  plum: '#3A0B42',
  coral: '#FF7D82',
  blush: '#FFF0F5',
  lavender: '#DCC7F5',
  roseGold: '#C9837A',
  ink: '#201224',
  muted: '#745E73',
  border: '#F0D9E5',
  white: '#FFFFFF',
  success: '#138A62',
  warning: '#BF6A00',
  danger: '#C92A4B',
} as const;

export type ColorToken = keyof typeof color;

/** Semantic aliases used by the customer, retailer and admin surfaces. */
export const semanticColor = {
  // Brand
  brandPrimary: color.pink,
  brandPrimaryHover: '#E23B63',
  brandContrast: color.plum,
  brandAccent: color.coral,
  brandSoft: color.blush,
  brandLavender: color.lavender,
  brandRoseGold: color.roseGold,

  // Text
  textPrimary: color.ink,
  textSecondary: color.muted,
  textOnBrand: color.white,
  textOnPlum: color.white,
  textLink: color.plum,

  // Surfaces
  /** Pure white, used for text on the plum portal sidebar. */
  surfaceWhite: color.white,
  surfaceCanvas: color.white,
  surfaceCanvasCustomer: color.blush,
  surfaceCard: color.white,
  surfaceRaised: color.white,
  surfaceInverse: color.plum,
  surfaceSubtle: '#FBF7FA',

  // Lines + focus
  borderDefault: color.border,
  borderStrong: '#E0BACD',
  focusRing: color.plum,

  // Status
  // Approved status hexes are used for fills, icons, borders and large display
  // numerals. They do not reach 4.5:1 against light surfaces as normal-size body
  // text, so each one has a darkened *Text companion that does. Never use the
  // fill token for small text.
  statusSuccess: color.success,
  statusSuccessSurface: '#E7F5EF',
  statusSuccessText: '#0B5C41',
  statusWarning: color.warning,
  statusWarningSurface: '#FBF0E2',
  statusWarningText: '#7A4400',
  statusDanger: color.danger,
  statusDangerSurface: '#FBE7EC',
  statusDangerText: '#A8203D',
  statusInfo: color.plum,
  statusInfoSurface: '#F3EAF6',
} as const;

export type SemanticColorToken = keyof typeof semanticColor;
